import { useIntl } from "@umijs/max";
import React, { useMemo } from "react";

interface RenderJsonProps {
  /** 当前 JSON 内容（字符串） */
  text: any;
  /** 对比的 JSON 内容（字符串） */
  compareText?: any;
  /** 是否是操作后的数据（影响提示文字） */
  isAfter?: boolean;
}

const RenderJson: React.FC<RenderJsonProps> = ({
  text,
  compareText,
  isAfter = false,
}) => {
  const intl = useIntl();

  const content = useMemo(() => {
    const val = text as string;
    if (!val || val === "-" || val === "null") return "null";

    try {
      // 尝试格式化 JSON 字符串
      const jsonObj = JSON.parse(val);

      // 如果解析后不是对象，直接返回
      if (typeof jsonObj !== "object" || jsonObj === null) {
        return val;
      }

      let compareObj: any = null;
      let isCompareNull = false;

      if (compareText && compareText !== "-" && compareText !== "null") {
        try {
          compareObj = JSON.parse(compareText);
          // 如果解析后是 null 或不是对象，视为对比基准为 null
          if (typeof compareObj !== "object" || compareObj === null) {
            isCompareNull = true;
          }
        } catch (e) {
          isCompareNull = true;
        }
      } else {
        // 如果对比文本不存在、为 "-" 或 "null"，视为操作前为 null
        isCompareNull = true;
      }

      /**
       * 递归渲染 JSON 对比
       * @param currVal 当前值
       * @param prevVal 对比值
       * @param isParentNew 是否为父级新增项
       * @param depth 递归深度
       */
      const renderRecursive = (
        currVal: any,
        prevVal: any,
        isParentNew: boolean,
        depth: number = 0,
      ): React.ReactNode => {
        const isPrimitive = currVal === null || typeof currVal !== "object";
        const isPrevPrimitive = prevVal === null || typeof prevVal !== "object";

        // 情况 1: 基本类型
        if (isPrimitive) {
          const changed =
            !isParentNew && JSON.stringify(currVal) !== JSON.stringify(prevVal);
          return (
            <span
              style={{
                backgroundColor: changed ? "#fff1f0" : "transparent",
                color: changed ? "#ff4d4f" : "inherit",
                fontWeight: changed ? "bold" : "normal",
              }}
            >
              {JSON.stringify(currVal)}
              {changed && !isPrevPrimitive && !isAfter && (
                <span
                  style={{
                    marginLeft: 4,
                    opacity: 0.6,
                    fontWeight: "normal",
                  }}
                >
                  {intl.formatMessage(
                    { id: "pages.system.logs.json.was" },
                    { value: JSON.stringify(prevVal) },
                  )}
                </span>
              )}
              {changed && (
                <span
                  style={{
                    marginLeft: 4,
                    opacity: 0.6,
                    fontWeight: "normal",
                  }}
                >
                  {intl.formatMessage({
                    id: isAfter
                      ? "pages.system.logs.json.added"
                      : "pages.system.logs.json.removed",
                  })}
                </span>
              )}
            </span>
          );
        }

        // 情况 2: 数组对比 (忽略位置，仅对比增删)
        if (Array.isArray(currVal)) {
          const prevArr = Array.isArray(prevVal) ? prevVal : [];
          const prevArrStrings = prevArr.map((i) => JSON.stringify(i));

          return (
            <span>
              [
              {currVal.map((item, i) => {
                const itemStr = JSON.stringify(item);
                const isItemDiff = !prevArrStrings.includes(itemStr);

                return (
                  <div key={`${i}-${itemStr}`} style={{ paddingLeft: 16 }}>
                    {isItemDiff ? (
                      <span
                        style={{
                          backgroundColor: "#fff1f0",
                          color: "#ff4d4f",
                          fontWeight: "bold",
                        }}
                      >
                        {itemStr}
                        <span
                          style={{
                            marginLeft: 4,
                            opacity: 0.6,
                            fontWeight: "normal",
                          }}
                        >
                          {intl.formatMessage({
                            id: isAfter
                              ? "pages.system.logs.json.added"
                              : "pages.system.logs.json.removed",
                          })}
                        </span>
                      </span>
                    ) : (
                      itemStr
                    )}
                    {i < currVal.length - 1 ? "," : ""}
                  </div>
                );
              })}
              ]
            </span>
          );
        }

        // 情况 3: 对象对比
        if (currVal && typeof currVal === "object") {
          const prevObj =
            prevVal && typeof prevVal === "object" && !Array.isArray(prevVal)
              ? prevVal
              : {};
          const keys = Object.keys(currVal);

          return (
            <span>
              {"{"}
              {keys.map((key, index) => {
                const hasPrev = Object.prototype.hasOwnProperty.call(
                  prevObj,
                  key,
                );
                const valChanged =
                  hasPrev &&
                  JSON.stringify(currVal[key]) !== JSON.stringify(prevObj[key]);
                const isNewKey = !hasPrev;

                return (
                  <div key={key} style={{ paddingLeft: 16 }}>
                    <span
                      style={{
                        fontWeight: isNewKey || valChanged ? "bold" : "normal",
                        color: isNewKey || valChanged ? "#ff4d4f" : "inherit",
                      }}
                    >
                      "{key}":
                    </span>{" "}
                    {isNewKey ? (
                      <span
                        style={{
                          backgroundColor: "#fff1f0",
                          color: "#ff4d4f",
                          fontWeight: "bold",
                        }}
                      >
                        {JSON.stringify(currVal[key])}
                        <span
                          style={{
                            marginLeft: 4,
                            opacity: 0.6,
                            fontWeight: "normal",
                          }}
                        >
                          {intl.formatMessage({
                            id: isAfter
                              ? "pages.system.logs.json.added"
                              : "pages.system.logs.json.removed",
                          })}
                        </span>
                      </span>
                    ) : (
                      renderRecursive(
                        currVal[key],
                        prevObj[key],
                        false,
                        depth + 1,
                      )
                    )}
                    {index < keys.length - 1 ? "," : ""}
                  </div>
                );
              })}
              {"}"}
            </span>
          );
        }

        return JSON.stringify(currVal);
      };

      return renderRecursive(jsonObj, compareObj, isCompareNull);
    } catch (e) {
      return val;
    }
  }, [text, compareText, isAfter, intl]);

  return (
    <pre
      style={{
        backgroundColor: "#f5f5f5",
        padding: "8px",
        borderRadius: "4px",
        margin: 0,
        overflow: "auto",
        fontSize: "12px",
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
        width: "100%",
      }}
    >
      {content}
    </pre>
  );
};

export default RenderJson;
